// api.ts - API service layer for all backend communications
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev';

class ApiService {
  private refreshPromise: Promise<any> | null = null;

  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = localStorage.getItem('carleads_auth_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private isTokenExpiringSoon(): boolean {
    const token = localStorage.getItem('carleads_auth_token');
    if (!token) return false;

    try {
      // Decode JWT to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      
      // Only refresh if token is still valid but expires in less than 1 day
      // Don't try to refresh if already expired (will cause 401 loop)
      return timeUntilExpiry > 0 && timeUntilExpiry < 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  }

  private async refreshTokenIfNeeded(): Promise<void> {
    if (!this.isTokenExpiringSoon()) {
      return;
    }

    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      await this.refreshPromise;
      return;
    }

    try {
      this.refreshPromise = fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: this.getHeaders(),
      }).then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.token) {
            localStorage.setItem('carleads_auth_token', data.data.token);
            return;
          }
        }
        // If refresh fails, clear token and redirect to login
        if (response.status === 401) {
          localStorage.removeItem('carleads_auth_token');
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
      }).catch(() => {
        // On any error, just ignore - the main request will handle it
      });

      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Check and refresh token if needed before request (but not for auth endpoints)
    if (!endpoint.includes('/auth/login') && !endpoint.includes('/auth/register')) {
      await this.refreshTokenIfNeeded();
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    });

    // If 401 on any endpoint, clear token and redirect to login
    if (response.status === 401) {
      localStorage.removeItem('carleads_auth_token');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `Request failed with status ${response.status}`);
    }

    return response.json();
  }

  // Auth API
  auth = {
    login: (email: string, password: string) =>
      this.request<any>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    register: (data: { email: string; password: string; companyName: string }) =>
      this.request<any>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    refresh: () =>
      this.request<any>('/api/auth/refresh', {
        method: 'POST',
      }),

    me: () => this.request<any>('/api/auth/me'),
  };

  // Leads API
  leads = {
    getAll: (params?: any) => {
      const query = params ? `?${new URLSearchParams(params)}` : '';
      return this.request<any>(`/api/leads${query}`);
    },

    getOne: (id: string) => this.request<any>(`/api/leads/${id}`),

    create: (data: any) =>
      this.request<any>('/api/leads', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: any) =>
      this.request<any>(`/api/leads/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<any>(`/api/leads/${id}`, {
        method: 'DELETE',
      }),

    match: (id: string, criteria: any) =>
      this.request<any>(`/api/leads/${id}/match`, {
        method: 'POST',
        body: JSON.stringify({ criteria }),
      }),

    getBestMatch: (id: string) =>
      this.request<any>(`/api/leads/${id}/best-match`),

    import: async (file: File, skipDuplicates: boolean) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('skipDuplicates', skipDuplicates.toString());

      const response = await fetch(`${API_BASE_URL}/api/leads/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('carleads_auth_token')}`,
        },
        body: formData,
      });

      return response.json();
    },

    export: async () => {
      const response = await fetch(`${API_BASE_URL}/api/leads/export`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('carleads_auth_token')}`,
        },
      });

      return response.blob();
    },

    getTemplate: async () => {
      const response = await fetch(`${API_BASE_URL}/api/leads/import/template`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('carleads_auth_token')}`,
        },
      });

      return response.blob();
    },
  };

  // Integrations API
  integrations = {
    getAll: () => this.request<any>('/api/integrations'),

    getOne: (id: string) => this.request<any>(`/api/integrations/${id}`),

    create: (data: any) =>
      this.request<any>('/api/integrations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: any) =>
      this.request<any>(`/api/integrations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<any>(`/api/integrations/${id}`, {
        method: 'DELETE',
      }),

    test: (id: string) =>
      this.request<any>(`/api/integrations/${id}/test`, {
        method: 'POST',
      }),

    sync: (id: string) =>
      this.request<any>(`/api/integrations/${id}/sync`, {
        method: 'POST',
      }),

    getLogs: (id: string, limit?: number) => {
      const query = limit ? `?limit=${limit}` : '';
      return this.request<any>(`/api/integrations/${id}/logs${query}`);
    },

    getStats: (id: string) =>
      this.request<any>(`/api/integrations/${id}/stats`),
  };

  // Notifications API
  notifications = {
    sendEmail: (data: any) =>
      this.request<any>('/api/notifications/email', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    sendSMS: (data: any) =>
      this.request<any>('/api/notifications/sms', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    sendPush: (data: any) =>
      this.request<any>('/api/notifications/push', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    subscribePush: (subscription: any) =>
      this.request<any>('/api/notifications/push/subscribe', {
        method: 'POST',
        body: JSON.stringify(subscription),
      }),

    unsubscribePush: (endpoint: string) =>
      this.request<any>('/api/notifications/push/unsubscribe', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      }),

    test: (type: string) =>
      this.request<any>('/api/notifications/test', {
        method: 'POST',
        body: JSON.stringify({ type }),
      }),

    getLogs: (type?: string, limit?: number) => {
      const params = new URLSearchParams();
      if (type) params.append('type', type);
      if (limit) params.append('limit', limit.toString());
      const query = params.toString() ? `?${params}` : '';
      return this.request<any>(`/api/notifications/logs${query}`);
    },

    getStats: () => this.request<any>('/api/notifications/stats'),
  };

  // Analytics API
  analytics = {
    getDashboard: () => this.request<any>('/api/analytics/dashboard'),
  };

  // Inventory API (to be implemented in backend)
  inventory = {
    getAll: () => this.request<any>('/api/inventory'),

    getOne: (id: string) => this.request<any>(`/api/inventory/${id}`),

    create: (data: any) =>
      this.request<any>('/api/inventory', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: any) =>
      this.request<any>(`/api/inventory/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<any>(`/api/inventory/${id}`, {
        method: 'DELETE',
      }),
  };

  // Real-time API
  realtime = {
    getNotifications: () =>
      this.request<any>('/api/realtime/notifications'),

    markNotificationRead: (id: string) =>
      this.request<any>(`/api/realtime/notifications/${id}/read`, {
        method: 'POST',
      }),

    getActivityFeed: (userId?: string) => {
      const params = userId ? `?userId=${userId}` : '';
      return this.request<any>(`/api/realtime/activity${params}`);
    },

    updatePresence: (status: 'online' | 'away' | 'offline', currentPage?: string) =>
      this.request<any>('/api/realtime/presence', {
        method: 'POST',
        body: JSON.stringify({ status, currentPage }),
      }),

    getPresence: (userId: string) =>
      this.request<any>(`/api/realtime/presence/${userId}`),

    getOnlineUsers: () =>
      this.request<any>('/api/realtime/presence/online'),
  };

  // Scrapers API
  scrapers = {
    getAll: () => this.request<any>('/api/scrapers'),

    getStatus: (source: string) =>
      this.request<any>(`/api/scrapers/${source}`),

    run: (source: string) =>
      this.request<any>(`/api/scrapers/${source}/run`, {
        method: 'POST',
      }),

    runAll: (sources?: string[]) =>
      this.request<any>('/api/scrapers/run-all', {
        method: 'POST',
        body: JSON.stringify({ sources }),
      }),

    getHistory: (source: string, limit?: number) => {
      const query = limit ? `?limit=${limit}` : '';
      return this.request<any>(`/api/scrapers/${source}/history${query}`);
    },

    cleanup: (daysToKeep?: number) =>
      this.request<any>('/api/scrapers/cleanup', {
        method: 'POST',
        body: JSON.stringify({ daysToKeep }),
      }),

    // Subscription methods
    getAvailable: () => this.request<any>('/api/scrapers/available'),

    getSubscriptions: () => this.request<any>('/api/scrapers/subscriptions'),

    subscribe: (source: string) =>
      this.request<any>('/api/scrapers/subscribe', {
        method: 'POST',
        body: JSON.stringify({ source }),
      }),

    unsubscribe: (source: string) =>
      this.request<any>(`/api/scrapers/unsubscribe/${source}`, {
        method: 'DELETE',
      }),

    toggleSubscription: (source: string, enabled: boolean) =>
      this.request<any>(`/api/scrapers/subscriptions/${source}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      }),
  };
}

export const api = new ApiService();
export default api;
